/**
 * Pure functions for computing tactical position coordinates from a formation.
 *
 * Extracted from the legacy Shadow Team feature's `useAction` hook and
 * utils (retired in SE-8371) but made pure (no hooks, no reactive vars, no
 * Canvas). Each function takes explicit dimensions
 * so the same math works for any container size.
 * @module features/squad-planning/utils/formationPositions
 */

import { ShadowTeamFormation } from "@/generated/graphql";

import { FORMATION_TO_DIGITS } from "../constants";
import type { SquadPlanningFormation, TacticalPosition } from "../types";

/** Player group identifier for Y-axis band lookup. */
type PlayerGroup = "DF" | "MF" | "FW" | "ST";

// ---------------------------------------------------------------------------
// Y-axis band per player group (0 = top / attacking, 1 = bottom / GK)
// ---------------------------------------------------------------------------

/**
 * Returns the fractional Y position for a player group in a formation.
 * @param group - The positional group (DF, MF, FW, ST)
 * @param formation - The formation to compute Y for
 * @returns Fractional Y position (0 = top, 1 = bottom)
 */
function getYBand(group: PlayerGroup, formation: ShadowTeamFormation): number {
  switch (group) {
    case "DF":
      return formation === ShadowTeamFormation.F_4132 ? 0.775 : 0.725;
    case "MF":
      return formation === ShadowTeamFormation.F_4132 ? 0.6 : 0.5;
    case "FW":
      return formation === ShadowTeamFormation.F_4231 ? 0.325 : 0.25;
    case "ST":
      return 0.15;
    default:
      return 1;
  }
}

// ---------------------------------------------------------------------------
// Role label helpers (mirrored from the retired shadow-team utils, SE-8371)
// ---------------------------------------------------------------------------

/** Lookup table for midfield roles by size. */
const MF_ROLE_BY_SIZE: Record<number, readonly string[]> = {
  1: ["CDM"],
  2: ["LCDM", "RCDM"],
  3: ["LCM", "CM", "RCM"],
};

/**
 * Returns the midfield role label based on position within the line.
 * @param position - 1-based index within the midfield line
 * @param size - Total number of midfielders in the formation
 * @param formation - The current formation
 * @returns Role abbreviation (e.g. "CM", "LM", "RM", "LCM")
 */
function getMfRole(
  position: number,
  size: number,
  formation: ShadowTeamFormation
): string {
  const lookup = MF_ROLE_BY_SIZE[size];
  if (lookup) return lookup[position - 1] ?? "CM";
  // size >= 4: wings + inner pair
  if (position === 1 && formation !== ShadowTeamFormation.F_4132) return "LM";
  if (position === size && formation !== ShadowTeamFormation.F_4132)
    return "RM";
  if (position === 2) return "LCM";
  if (position === size - 1) return "RCM";
  return "CM";
}

/**
 * Returns the defender role label based on position within the line.
 * @param position - 1-based index within the defensive line
 * @param size - Total number of defenders in the formation
 * @returns Role abbreviation (e.g. "CB", "LB", "RB", "LCB", "RCB")
 */
function getDfRole(position: number, size: number): string {
  if (size === 3) {
    if (position === 1) return "LCB";
    if (position === 3) return "RCB";
    return "CB";
  }
  if (position === 1) return "LB";
  if (position === size) return "RB";
  if (size === 5) {
    if (position === 2) return "LCB";
    if (position === 4) return "RCB";
    return "CB";
  }
  // size === 4: inner pair
  return position === 2 ? "LCB" : "RCB";
}

/**
 * Returns the forward role label based on position within the line.
 * @param position - 1-based index within the forward line
 * @param size - Total number of forwards in the formation
 * @param formation - The current formation
 * @returns Role abbreviation (e.g. "ST", "LW", "RW", "CAM", "CM")
 */
function getFwRole(
  position: number,
  size: number,
  formation: ShadowTeamFormation
): string {
  if (size === 2) {
    if (formation === ShadowTeamFormation.F_4132) return "CM";
    return position === 1 ? "LST" : "RST";
  }
  if (
    (position === 1 || position === size) &&
    formation === ShadowTeamFormation.F_4132
  )
    return "CM";
  if (position === 1 && size > 2) return "LW";
  if (position === size && size > 2) return "RW";
  if (position === 2 && size === 3 && formation !== ShadowTeamFormation.F_4231)
    return "ST";
  return "CAM";
}

/**
 * Resolves the role label for any positional group.
 * @param group - The positional group (DF, MF, FW, ST)
 * @param position - 1-based index within the group line
 * @param size - Total players in that group line
 * @param formation - The current formation
 * @returns Role abbreviation string
 */
function getRoleLabel(
  group: PlayerGroup,
  position: number,
  size: number,
  formation: ShadowTeamFormation
): string {
  switch (group) {
    case "ST":
      if (size >= 2) return position === 1 ? "LST" : "RST";
      return "ST";
    case "FW":
      return getFwRole(position, size, formation);
    case "MF":
      return getMfRole(position, size, formation);
    case "DF":
      return getDfRole(position, size);
    default:
      return "GK";
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Computes tactical position coordinates for every slot in a formation.
 *
 * Returns normalised coordinates (0–1 range) so the caller can multiply
 * by the actual container width/height. GK is always index 0 at the
 * bottom-centre of the field.
 * @param formation - The formation enum value (e.g. F_433)
 * @returns Array of {@link TacticalPosition} with normalised x/y
 */
export function getFormationPositions(
  formation: SquadPlanningFormation
): readonly TacticalPosition[] {
  const digits = FORMATION_TO_DIGITS[formation];
  if (!digits) return [];

  const arr = digits.split("").map(Number).filter(Boolean);

  const groups: readonly PlayerGroup[] = ["DF", "MF", "FW", "ST"];

  // GK always at bottom-centre (0.88 leaves room for role label below)
  const gk: TacticalPosition = {
    index: 0,
    x: 0.5,
    y: 0.85,
    role: "GK",
  };

  // Pre-compute cumulative index offsets for each line
  // Line 0 starts at 1 (after GK=0), line 1 starts at 1+arr[0], etc.
  const offsets = arr.reduce<readonly number[]>(
    (acc, _count, idx) => [
      ...acc,
      idx === 0 ? 1 : (acc[acc.length - 1] ?? 0) + arr[idx - 1],
    ],
    []
  );

  const outfield = arr.flatMap((count, lineIdx) => {
    const group =
      count === 1 && lineIdx === 2 && arr.length === 3
        ? ("ST" as const)
        : groups[lineIdx];

    const yBand = getYBand(group, formation as ShadowTeamFormation);
    const baseIndex = offsets[lineIdx] ?? 1;

    return Array.from({ length: count }, (_, i) => {
      const xSpacing = 1 / (count + 1);
      return {
        index: baseIndex + i,
        x: xSpacing * (i + 1),
        y: yBand,
        role: getRoleLabel(
          group,
          i + 1,
          count,
          formation as ShadowTeamFormation
        ),
      } satisfies TacticalPosition;
    });
  });

  return [gk, ...outfield];
}

/**
 * Parses a formation enum value into an array of line counts.
 * E.g. F_433 → [4, 3, 3], F_4231 → [4, 2, 3, 1].
 * @param formation - The formation enum value
 * @returns Array of player counts per line (defense → attack)
 */
export function getFormationLines(
  formation: SquadPlanningFormation
): readonly number[] {
  const digits = FORMATION_TO_DIGITS[formation];
  if (!digits) return [];
  return digits.split("").map(Number).filter(Boolean);
}
